class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);

    // Configuration for triggers
    this.config = {
      triggerType: 'exitIntent', // Options: 'immediate', 'exitIntent', 'delayed'
      delayTime: 30000, // 30 seconds for delayed trigger
      disableOnPages: ['/cart', '/checkout', '/checkouts'], // Pages to disable popup
    };

    // State management
    this.hasShownNotification = false;
    this.exitIntentBound = false;
    this.delayTimeout = null;
    this.pendingCartData = null;

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );

    // Set up exit intent listener
    this.setupExitIntent = this.setupExitIntent.bind(this);
    this.handleMouseLeave = this.handleMouseLeave.bind(this);
  }

  shouldShowNotification() {
    // Check if we're on a disabled page
    const currentPath = window.location.pathname;
    return !this.config.disableOnPages.some(path => currentPath.includes(path));
  }

  setupExitIntent() {
    if (this.exitIntentBound || !this.shouldShowNotification()) return;

    this.exitIntentBound = true;
    document.addEventListener('mouseout', this.handleMouseLeave);
  }

  handleMouseLeave(e) {
    // Detect if mouse is leaving viewport from the top
    if (e.clientY <= 0 && !this.hasShownNotification && this.pendingCartData) {
      this.showPendingNotification();
    }
  }

  setupDelayedTrigger() {
    if (!this.shouldShowNotification()) return;

    if (this.delayTimeout) {
      clearTimeout(this.delayTimeout);
    }

    this.delayTimeout = setTimeout(() => {
      if (!this.hasShownNotification && this.pendingCartData) {
        this.showPendingNotification();
      }
    }, this.config.delayTime);
  }

  showPendingNotification() {
    if (this.pendingCartData && this.shouldShowNotification()) {
      this.hasShownNotification = true;
      this.actuallyRenderAndOpen(this.pendingCartData);
      this.pendingCartData = null;

      // Clean up listeners
      document.removeEventListener('mouseout', this.handleMouseLeave);
      if (this.delayTimeout) {
        clearTimeout(this.delayTimeout);
      }
    }
  }

  open() {
    if (!this.shouldShowNotification()) return;

    this.notification.classList.add('animate', 'active');

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);
  }

  close() {
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  actuallyRenderAndOpen(parsedState) {
    this.cartItemKey = parsedState.key;
    this.getSectionsToRender().forEach((section) => {
      document.getElementById(section.id).innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    if (this.header) this.header.reveal();
    this.open();
  }

  renderContents(parsedState) {
    if (!this.shouldShowNotification()) {
      // Update cart bubble but don't show notification
      const cartBubble = document.getElementById('cart-icon-bubble');
      if (cartBubble && parsedState.sections['cart-icon-bubble']) {
        cartBubble.innerHTML = this.getSectionInnerHTML(
          parsedState.sections['cart-icon-bubble'],
          '.shopify-section'
        );
      }
      return;
    }

    // Store the cart data
    this.pendingCartData = parsedState;

    // Determine trigger behavior
    if (this.config.triggerType === 'immediate') {
      // Original behavior - show immediately
      this.showPendingNotification();
    } else if (this.config.triggerType === 'exitIntent') {
      // Set up exit intent detection
      this.setupExitIntent();
      // Also set up delayed trigger as fallback
      this.setupDelayedTrigger();
    } else if (this.config.triggerType === 'delayed') {
      // Only use time-based trigger
      this.setupDelayedTrigger();
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
